import {
  CategoryIcon,
  DeleteIconThin,
  NoCategoryIcon,
} from '@/assets/svg'
import { Pagination } from '@/components/fragments'
import WrapperCard from '@/components/fragments/wrapper-card'
import { DeleteModal } from '@/components/modals'
import { Button } from '@/components/ui/button'
import {
  useCreateBusinessTypeSubcategoryMutation,
  useDeleteBusinessTypeSubcategoryMutation,
  useGetBusinessTypeListQuery,
  useUpdateBusinessTypeSubcategoryMutation,
} from '@/redux/services/settings/business-types'
import { MenuCategoriesSkeleton } from '@/skeletons'
import { useDebounce } from '@/utils/hooks/useDebounce'
import { Plus } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import AddCategoryModal from './modals/add-category-modal'
import BusinessTypeCategory from './business-type-category'

interface Category {
  id: string
  name: string
  description?: string
  image_url?: string
  position?: number
  is_published: boolean
  active?: boolean
  vendor_count?: number
  business_type_id?: number
  business_type_name?: string
}

const MenuCategories = () => {
  const [showAddModal, setShowAddModal] = useState(false)
  const [editingCategory, setEditingCategory] = useState<Category | null>(null)
  const [preselectedBusinessTypeId, setPreselectedBusinessTypeId] = useState<number | undefined>(undefined)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [categoryToDelete, setCategoryToDelete] = useState<any>(
    null
  )
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize] = useState(15)
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('')

  const { data: businessTypesData, isLoading } = useGetBusinessTypeListQuery(
    {
      page: currentPage,
      per_page: pageSize,
      search: debouncedSearchQuery,
    },
    {
      refetchOnMountOrArgChange: true,
      refetchOnReconnect: true,
      refetchOnFocus: true,
    }
  )

  const [createSubcategory, { isLoading: isCreatingSubcategory }] =
    useCreateBusinessTypeSubcategoryMutation()
  const [updateSubcategory, { isLoading: isUpdatingSubcategory }] =
    useUpdateBusinessTypeSubcategoryMutation()
  const [deleteSubcategory, { isLoading: isDeletingSubcategory }] =
    useDeleteBusinessTypeSubcategoryMutation()

  const handleSearch = (value: string) => {
    setDebouncedSearchQuery(value)
    setCurrentPage(1)
  }

  const debouncedSearch = useDebounce(handleSearch, 500)


  const handleInputChange = (value: string) => {
    setSearchQuery(value)
    debouncedSearch(value)
  }

  const handleDeleteCategory = (item: any) => {
    const categoryToDelete = {
      id: String(item.id),
    }
    setCategoryToDelete(categoryToDelete)
    setShowDeleteModal(true)
  }

  const handleConfirmDelete = async () => {
    if (!categoryToDelete) return

    try {
      await deleteSubcategory(categoryToDelete.id)
        .unwrap()
        .then((res: any) => {
          if (res.success) {
            toast.success(res.message || res.data?.message)
            setShowDeleteModal(false)
            setCategoryToDelete(null)
          } else {
            toast.error(res.message || res.data?.message)
          }
        })
        .catch((err: any) => {
          toast.error(err.data?.message || err.message || 'Failed to delete sub-category')
        })
    } catch (error) {
      console.error('Error deleting sub-category:', error)
      toast.error('Failed to delete sub-category')
    }
  }

  const handleCancelDelete = () => {
    setShowDeleteModal(false)
    setCategoryToDelete(null)
  }

  const handleAddCategory = (businessTypeId?: number) => {
    if (businessTypeId) {
      setPreselectedBusinessTypeId(businessTypeId)
    }
    setEditingCategory(null)
    setShowAddModal(true)
  }

  const handleEditCategory = (item: any) => {
    const categoryToEdit: Category = {
      id: String(item.id),
      name: item.name,
      image_url: item.image_url,
      position: item.position,
      is_published: item.is_published ?? false,
      business_type_id: item.business_type_id,
      business_type_name: item.business_type_name,
      active: item.active,
      vendor_count: item.vendor_count,
    }
    setEditingCategory(categoryToEdit)
    setPreselectedBusinessTypeId(undefined)
    setShowAddModal(true)
  }


  const handleAddCategorySubmit = async (data: {
    name: string
    image?: File
    business_type_id?: number
    position?: number
    active?: boolean
    is_published?: boolean
  }) => {
    const isEdit = !!editingCategory
    
    const formData = new FormData()
    
    if (isEdit) {
      formData.append('id', editingCategory!.id)
      formData.append('_method', 'PUT')
    }
    
    formData.append('name', data.name)
    formData.append('business_type_id', String(data.business_type_id || ''))
    
    if (data.position !== undefined) {
      formData.append('position', String(data.position))
    }
    
    formData.append('active', data.active !== false ? '1' : '0')
    formData.append('is_published', data.is_published ? '1' : '0')
    
    if (data.image) {
      formData.append('image', data.image)
    }

    try {
      const mutation = isEdit ? updateSubcategory : createSubcategory
      await mutation(formData)
        .unwrap()
        .then((res: any) => {
          if (res.success) {
            toast.success(res.message || res.data?.message)
            setCurrentPage(1)
            setShowAddModal(false)
            setEditingCategory(null)
            setPreselectedBusinessTypeId(undefined)
          } else {
            toast.error(res.message || res.data?.message)
          }
        })
        .catch((err: any) => {
          toast.error(err.data?.message || err.message || `Failed to ${isEdit ? 'update' : 'create'} sub-category`)
        })
    } catch (error) {
      console.error(`Error ${isEdit ? 'updating' : 'creating'} sub-category:`, error)
      toast.error(`Failed to ${isEdit ? 'update' : 'create'} sub-category`)
    }
  }


  return (
    <div className='space-y-6 mt-4'>
      <WrapperCard
        title='Current Sub-Categories'
        icon={CategoryIcon}
        wrapperClassName='h-fit relative mb-0'
        headerClassName='flex flex-row-reverse justify-end items-center gap-2 text-[#424242]'
      >
          <div className='flex justify-end items-center gap-x-3 w-fit absolute top-5 right-5'>
            {/* <div className='w-64'>
              <Input
                placeholder='Search sub-category name'
                className='w-full rounded-[8px]'
                id='search'
                name='search'
                value={searchQuery}
                onChange={(e) => handleInputChange(e.target.value)}
              />
            </div> */}
            <div>
              <Button
                onClick={handleAddCategory}
                className='bg-primary hover:bg-primary/90 text-primary-foreground p-2 sm:p-3 sm:px-4 sm:py-2 rounded-[8px] flex items-center space-x-2'
              >
                <Plus className='h-4 w-4' />
                <span className='hidden sm:block'>Add Category</span>
              </Button>
            </div>
          </div>

      </WrapperCard>

      <div className=''>
        {isLoading ? (
          <MenuCategoriesSkeleton />
        ) : (
          <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
          {
            businessTypesData?.business_types.map((businessType: any, index: number) => 
                <BusinessTypeCategory
                  key={index}
                  title={businessType.name}
                  icon={CategoryIcon}
                  items={businessType.subcategories}
                  onAdd={() => handleAddCategory(businessType.id)}
                  renderEmptyState={() => (
                    <div className='flex flex-col items-center justify-center py-12 px-4'>
                      <div className='w-[72px] h-[72px] text-[#D9D9D9] bg-[#F5F5F5] rounded-full p-3 flex items-center justify-center'>
                        {NoCategoryIcon}
                      </div>
                      <p className='text-sm text-gray-500 text-center lg:text-lg font-medium '>{`Canopy ${index + 1} has no category`}</p>
                      <div className='flex items-center justify-center'>
                        <Button
                          onClick={() => handleAddCategory(businessType.id)}
                          className='bg-primary hover:bg-primary/90 text-primary-foreground w-34 flex items-center justify-center mt-3'
                        >
                          <Plus className='h-5 w-5' />
                          <span className='hidden sm:block'>Add</span>
                        </Button>
                      </div>
                    </div>
                  )}
                  onItemEdit={handleEditCategory}
                  onItemDelete={handleDeleteCategory}
                  showEdit={true}
                  showDelete={true}
                />
            ) }
          </div>
        )}
      </div>

      {/* Pagination */}
      {businessTypesData?.pagination && (
        <Pagination
          currentPage={currentPage}
          totalPages={businessTypesData.pagination.last_page}
          totalItems={businessTypesData.pagination.total}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
        />
      )}

      <AddCategoryModal
        show={showAddModal}
        handleClose={() => {
          setShowAddModal(false)
          setEditingCategory(null)
          setPreselectedBusinessTypeId(undefined)
        }}
        onSubmit={handleAddCategorySubmit}
        loading={isCreatingSubcategory || isUpdatingSubcategory}
        editingItem={editingCategory}
        preselectedBusinessTypeId={preselectedBusinessTypeId}
      />

      <DeleteModal
        show={showDeleteModal}
        handleClose={handleCancelDelete}
        title='Are you sure you want to delete this sub-category?'
        itemName={categoryToDelete?.name || ''}
        actionBtnLoading={isDeletingSubcategory}
        handleDelete={handleConfirmDelete}
        icon={
          <div className='w-[72px] h-[72px] text-[#ED544E] bg-red-50 rounded-full p-3 flex items-center justify-center'>
            {DeleteIconThin}
          </div>
        }
      />
    </div>
  )
}

export default MenuCategories
